import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, Edit, Trash2, Package, Search, ShoppingBag,
    X, Save, Check, Boxes, Image as ImageIcon,
    Send, Upload, FileText, Zap, IndianRupee, Settings, HelpCircle, ArrowRight, Bell, Inbox,
    Percent, TrendingUp, Loader2
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const VendorProducts = () => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('inventory');
    const [globalProducts, setGlobalProducts] = useState([]);
    const [myInventory, setMyInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSuggestModal, setShowSuggestModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestTab, setSuggestTab] = useState('basic');

    const [inventoryForm, setInventoryForm] = useState({
        product_id: '',
        quantity: '',
        location: ''
    });
    const [commissionInfo, setCommissionInfo] = useState(null);

    // Vendor product suggestion form (for admin approval)
    const [suggestionForm, setSuggestionForm] = useState({
        // Basic Info
        name: '',
        brand: '',
        category: 'home',
        system_size_kw: '',
        suggested_price: '',
        suggested_price: '',
        warranty_years: '',
        subsidy_eligible: false,

        // Description (Vendor adds this)
        short_description: '',
        long_description: '',

        // Images (Vendor adds this)
        main_image_url: '',
        gallery_images: ['', '', ''],

        // Features (Vendor adds this)
        key_features: ['', '', '', ''],
        specifications: [
            { label: 'Wattage', value: '' },
            { label: 'Efficiency', value: '' },
            { label: 'Dimensions', value: '' },
            { label: 'Weight', value: '' }
        ]
    });

    const location = useLocation();

    useEffect(() => {
        if (location.state?.action === 'browse_catalog') {
            setActiveTab('browse');
        } else if (location.state?.action === 'suggest_product') {
            setShowSuggestModal(true);
        }
        
        // Clear state to avoid re-triggering on refresh
        if (location.state?.action) {
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [productsRes, inventoryRes, commissionRes] = await Promise.all([
                axios.get(`${API}/api/products`),
                axios.get(`${API}/api/vendor/inventory`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API}/api/vendor/commission`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: null }))
            ]);
            setGlobalProducts(productsRes.data);
            setMyInventory(inventoryRes.data);
            if (commissionRes.data) setCommissionInfo(commissionRes.data);
        } catch (error) {
            console.log('Using demo data');
            setGlobalProducts([
                { id: '1', name: 'Solar Panel 400W Mono PERC', category: 'home', system_size_kw: 0.4, price: 8500, brand: 'Tata Power Solar', image_url: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400' },
                { id: '2', name: '5kW Complete Home Solar System', category: 'home', system_size_kw: 5, price: 225000, brand: 'Luminous', image_url: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=400' },
                { id: '3', name: '10kW Commercial Solar Array', category: 'commercial', system_size_kw: 10, price: 450000, brand: 'Adani Solar', image_url: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=400' },
                { id: '4', name: 'Lithium Battery Storage 10kWh', category: 'battery', system_size_kw: 10, price: 95000, brand: 'Tesla', image_url: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=400' },
                { id: '5', name: 'Hybrid Inverter 5kW', category: 'inverter', system_size_kw: 5, price: 45000, brand: 'Growatt', image_url: 'https://images.unsplash.com/photo-1545209463-e2a9e07c8693?w=400' },
            ]);
            setMyInventory([
                { id: 'inv1', product_id: '1', product_name: 'Solar Panel 400W Mono PERC', quantity: 50, vendor_price: 7500, sell_price: 8500, is_available: true },
                { id: 'inv2', product_id: '2', product_name: '5kW Complete Home Solar System', quantity: 10, vendor_price: 200000, sell_price: 225000, is_available: true },
            ]);
        }
        setLoading(false);
    };

    const handleAddToInventory = (product) => {
        setSelectedProduct(product);
        setInventoryForm({ product_id: product.id, quantity: '', location: '' });
        setShowAddModal(true);
    };

    const handleSubmitInventory = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/api/vendor/inventory`, {
                product_id: inventoryForm.product_id,
                quantity: parseInt(inventoryForm.quantity),
                location: inventoryForm.location || null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Product added to your inventory!');
            setShowAddModal(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add product');
        }
    };

    const handleUpdateInventory = async (item, updates) => {
        try {
            await axios.put(`${API}/api/vendor/inventory/${item.id}`, updates, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Inventory updated!');
            fetchData();
        } catch (error) {
            setMyInventory(myInventory.map(i => i.id === item.id ? { ...i, ...updates } : i));
            toast.success('Inventory updated!');
        }
    };

    const handleRemoveFromInventory = async (item) => {
        if (!window.confirm(`Remove "${item.product_name}" from your inventory?`)) return;
        try {
            await axios.delete(`${API}/api/vendor/inventory/${item.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Product removed from inventory');
            fetchData();
        } catch (error) {
            setMyInventory(myInventory.filter(i => i.id !== item.id));
            toast.success('Product removed from inventory');
        }
    };

    const handleSubmitSuggestion = async (e) => {
        e.preventDefault();
        try {
            // Build payload with only expected fields
            const payload = {
                name: suggestionForm.name,
                brand: suggestionForm.brand || "",
                category: suggestionForm.category || "home",
                system_size_kw: parseFloat(suggestionForm.system_size_kw) || 0,
                suggested_price: parseFloat(suggestionForm.suggested_price) || 0,
                warranty_years: parseInt(suggestionForm.warranty_years) || 0,
                subsidy_eligible: Boolean(suggestionForm.subsidy_eligible),
                short_description: suggestionForm.short_description || "",
                long_description: suggestionForm.long_description || "",
                main_image_url: suggestionForm.main_image_url || "",
                gallery_images: suggestionForm.gallery_images.filter(i => i && i.trim()),
                key_features: suggestionForm.key_features.filter(f => f && f.trim()),
                specifications: suggestionForm.specifications.filter(s => s.label && s.value && s.value.trim())
            };

            console.log('Submitting product suggestion:', payload);

            await axios.post(`${API}/api/vendor/suggest-product`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Product submitted for admin approval!');
            setShowSuggestModal(false);
            resetSuggestionForm();
        } catch (error) {
            console.error('Submission error:', error.response?.data || error.message);
            // Handle Pydantic validation errors (array of objects with loc, msg, type)
            const detail = error.response?.data?.detail;
            let errorMsg = 'Failed to submit product';
            if (Array.isArray(detail)) {
                // Extract field name from 'loc' and message from 'msg'
                errorMsg = detail.map(e => {
                    const field = e.loc ? e.loc[e.loc.length - 1] : 'unknown';
                    return `${field}: ${e.msg}`;
                }).join(', ');
            } else if (typeof detail === 'string') {
                errorMsg = detail;
            }
            toast.error(errorMsg);
        }
    };

    const resetSuggestionForm = () => {
        setSuggestionForm({
            name: '', brand: '', category: 'home', system_size_kw: '',
            suggested_price: '', warranty_years: '',
            short_description: '', long_description: '',
            main_image_url: '', gallery_images: ['', '', ''],
            key_features: ['', '', '', ''],
            specifications: [
                { label: 'Wattage', value: '' },
                { label: 'Efficiency', value: '' },
                { label: 'Dimensions', value: '' },
                { label: 'Weight', value: '' }
            ]
        });
        setSuggestTab('basic');
    };

    const updateFeature = (index, value) => {
        const newFeatures = [...suggestionForm.key_features];
        newFeatures[index] = value;
        setSuggestionForm({ ...suggestionForm, key_features: newFeatures });
    };

    const updateSpec = (index, field, value) => {
        const newSpecs = [...suggestionForm.specifications];
        newSpecs[index] = { ...newSpecs[index], [field]: value };
        setSuggestionForm({ ...suggestionForm, specifications: newSpecs });
    };

    const updateGalleryImage = (index, value) => {
        const newGallery = [...suggestionForm.gallery_images];
        newGallery[index] = value;
        setSuggestionForm({ ...suggestionForm, gallery_images: newGallery });
    };

    const inventoryProductIds = myInventory.map(i => i.product_id);
    const availableProducts = globalProducts.filter(p =>
        !inventoryProductIds.includes(p.id) &&
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const suggestTabs = [
        { id: 'basic', label: 'Basic Info', icon: Package },
        { id: 'description', label: 'Description', icon: FileText },
        { id: 'images', label: 'Images', icon: ImageIcon },
        { id: 'features', label: 'Features', icon: Zap }
    ];

    return (
        <DashboardLayout userRole="vendor">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold">My Products</h1>
                        <p className="text-muted-foreground mt-1">Manage your inventory and suggest new products</p>
                    </div>
                    <Button onClick={() => setShowSuggestModal(true)} className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" /> Suggest New Product
                    </Button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b">
                    <button
                        onClick={() => setActiveTab('inventory')}
                        className={`px-4 py-3 font-medium flex items-center gap-2 transition-colors ${activeTab === 'inventory'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <Boxes className="w-4 h-4" /> My Inventory ({myInventory.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('browse')}
                        className={`px-4 py-3 font-medium flex items-center gap-2 transition-colors ${activeTab === 'browse'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <ShoppingBag className="w-4 h-4" /> Browse Catalog ({availableProducts.length})
                    </button>
                </div>

                {/* My Inventory Tab */}
                {activeTab === 'inventory' && (
                    <div className="space-y-4">
                        {myInventory.length === 0 ? (
                            <div className="text-center py-12 bg-card rounded-xl border">
                                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="font-semibold text-lg">No Products Yet</h3>
                                <p className="text-muted-foreground mt-1">Browse the catalog or suggest your own products</p>
                                <div className="flex gap-2 justify-center mt-4">
                                    <Button onClick={() => setActiveTab('browse')} variant="outline">Browse Catalog</Button>
                                    <Button onClick={() => setShowSuggestModal(true)}>Suggest Product</Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Commission Info Banner */}
                                {commissionInfo && (
                                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-indigo-100">
                                            <Percent className="w-5 h-5 text-indigo-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-indigo-800 text-sm">Your Commission Rate: <span className="text-lg font-bold">{commissionInfo.commission_rate}%</span></p>
                                            <p className="text-xs text-indigo-600">You earn {(100 - commissionInfo.commission_rate).toFixed(1)}% of every sale. Funds are held for {commissionInfo.settlement_hold_days} days after delivery, auto-settled after {commissionInfo.auto_settle_days} days.</p>
                                        </div>
                                    </div>
                                )}

                                <div className="bg-card rounded-xl border overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium">List Price</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium">Your Earning / Unit</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium">Commission</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium">Stock</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myInventory.map((item) => {
                                                const rate = commissionInfo?.commission_rate || 5;
                                                const earning = item.sell_price * (1 - rate / 100);
                                                const commission = item.sell_price * (rate / 100);
                                                return (
                                                    <tr key={item.id} className="border-t hover:bg-muted/30">
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium">{item.product_name}</p>
                                                        </td>
                                                        <td className="px-4 py-3 font-semibold">₹{item.sell_price?.toLocaleString()}</td>
                                                        <td className="px-4 py-3">
                                                            <span className="font-semibold text-green-600">₹{earning.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge className="bg-violet-100 text-violet-700">
                                                                ₹{commission.toLocaleString(undefined, {maximumFractionDigits: 0})} ({rate}%)
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={item.quantity > 0 ? 'text-green-600' : 'text-red-500'}>
                                                                {item.quantity} units
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge
                                                                className={`cursor-pointer ${item.is_available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                                                                onClick={() => handleUpdateInventory(item, { is_available: !item.is_available })}
                                                            >
                                                                {item.is_available ? 'Active' : 'Paused'}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleRemoveFromInventory(item)}
                                                                className="text-red-600 hover:text-red-700"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Browse Products Tab */}
                {activeTab === 'browse' && (
                    <div className="space-y-4">
                        <div className="relative max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {availableProducts.map((product) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-card rounded-xl border overflow-hidden hover:shadow-md transition-shadow"
                                >
                                    <div className="aspect-video bg-muted">
                                        <img
                                            src={product.image_url || 'https://via.placeholder.com/400x300'}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <Badge variant="outline" className="mb-2 capitalize">{product.category}</Badge>
                                        <h3 className="font-semibold line-clamp-2">{product.name}</h3>
                                        <p className="text-sm text-muted-foreground">{product.brand} • {product.system_size_kw} kW</p>
                                        <div className="flex items-center justify-between mt-4">
                                            <div>
                                                <p className="text-xs text-muted-foreground">List Price</p>
                                                <p className="text-xl font-bold">₹{product.price.toLocaleString()}</p>
                                            </div>
                                            <Button onClick={() => handleAddToInventory(product)} size="sm">
                                                <Plus className="w-4 h-4 mr-1" /> Add
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {availableProducts.length === 0 && (
                            <div className="text-center py-12 bg-muted/30 rounded-xl">
                                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                <h3 className="font-semibold">All Products Added</h3>
                                <p className="text-muted-foreground">Suggest your own products to grow your catalog</p>
                                <Button onClick={() => setShowSuggestModal(true)} className="mt-4">
                                    <Plus className="w-4 h-4 mr-2" /> Suggest Product
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Add to Inventory Modal */}
                <AnimatePresence>
                    {showAddModal && selectedProduct && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                            onClick={() => setShowAddModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-md"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b">
                                    <h2 className="text-xl font-bold">Add to Inventory</h2>
                                    <p className="text-sm text-muted-foreground mt-1">{selectedProduct.name}</p>
                                </div>
                                <form onSubmit={handleSubmitInventory} className="p-6 space-y-4">
                                    <div className="p-4 bg-primary/10 rounded-lg">
                                        <p className="text-sm text-muted-foreground">List Price</p>
                                        <p className="text-2xl font-bold">₹{selectedProduct.price.toLocaleString()}</p>
                                    </div>

                                    {/* Commission Info */}
                                    {commissionInfo && (
                                        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-3">
                                            <div className="flex items-center gap-2 text-indigo-800 font-semibold">
                                                <Percent className="w-4 h-4" />
                                                <span>Your Commission Rate: {commissionInfo.commission_rate}%</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="bg-white rounded-lg p-3 text-center border">
                                                    <p className="text-xs text-muted-foreground">Platform Fee</p>
                                                    <p className="text-lg font-bold text-violet-700">
                                                        ₹{(selectedProduct.price * commissionInfo.commission_rate / 100).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                    </p>
                                                </div>
                                                <div className="bg-white rounded-lg p-3 text-center border">
                                                    <p className="text-xs text-muted-foreground">You Earn / Unit</p>
                                                    <p className="text-lg font-bold text-emerald-600">
                                                        ₹{(selectedProduct.price * (1 - commissionInfo.commission_rate / 100)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-indigo-400">
                                                Funds available after {commissionInfo.settlement_hold_days}-day hold period.
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label>Quantity in Stock</Label>
                                        <Input
                                            type="number"
                                            placeholder="How many do you have?"
                                            value={inventoryForm.quantity}
                                            onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })}
                                            required
                                            min="1"
                                        />
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">Cancel</Button>
                                        <Button type="submit" className="flex-1 btn-primary">Add to Inventory</Button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Suggest Product Modal - For Vendors */}
                <AnimatePresence>
                    {showSuggestModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
                            onClick={() => setShowSuggestModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 20 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-3xl my-8"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-card rounded-t-2xl z-10">
                                    <div>
                                        <h2 className="text-xl font-bold">Suggest New Product</h2>
                                        <p className="text-sm text-muted-foreground">Add details for admin approval</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setShowSuggestModal(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>

                                {/* Tab Navigation */}
                                <div className="border-b bg-muted/30 overflow-x-auto">
                                    <div className="flex">
                                        {suggestTabs.map((tab) => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setSuggestTab(tab.id)}
                                                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors border-b-2 ${suggestTab === tab.id
                                                    ? 'text-primary border-primary bg-card'
                                                    : 'text-muted-foreground border-transparent hover:text-foreground'
                                                    }`}
                                            >
                                                <tab.icon className="w-4 h-4" />
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <form onSubmit={handleSubmitSuggestion}>
                                    <div className="p-6 min-h-[350px]">
                                        {/* Basic Info Tab */}
                                        {suggestTab === 'basic' && (
                                            <div className="space-y-4">
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Product Name *</Label>
                                                        <Input
                                                            placeholder="e.g., Solar Panel 400W Mono PERC"
                                                            value={suggestionForm.name}
                                                            onChange={(e) => setSuggestionForm({ ...suggestionForm, name: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Brand *</Label>
                                                        <Input
                                                            placeholder="e.g., Tata Power Solar"
                                                            value={suggestionForm.brand}
                                                            onChange={(e) => setSuggestionForm({ ...suggestionForm, brand: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid md:grid-cols-3 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Category *</Label>
                                                        <Select value={suggestionForm.category} onValueChange={(v) => setSuggestionForm({ ...suggestionForm, category: v })}>
                                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="home">Home Solar</SelectItem>
                                                                <SelectItem value="commercial">Commercial</SelectItem>
                                                                <SelectItem value="inverter">Inverters</SelectItem>
                                                                <SelectItem value="battery">Batteries</SelectItem>
                                                                <SelectItem value="accessory">Accessories</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>System Size (kW)</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            value={suggestionForm.system_size_kw}
                                                            onChange={(e) => setSuggestionForm({ ...suggestionForm, system_size_kw: e.target.value })}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Warranty (Years)</Label>
                                                        <Input
                                                            type="number"
                                                            value={suggestionForm.warranty_years}
                                                            onChange={(e) => setSuggestionForm({ ...suggestionForm, warranty_years: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Suggested Price (₹) *</Label>
                                                    <Input
                                                        type="number"
                                                        placeholder="Your suggested selling price"
                                                        value={suggestionForm.suggested_price}
                                                        onChange={(e) => setSuggestionForm({ ...suggestionForm, suggested_price: e.target.value })}
                                                        required
                                                    />

                                                    <p className="text-xs text-muted-foreground">Admin will set the final selling price</p>
                                                </div>

                                                <div className="flex items-center space-x-2 border p-3 rounded-lg bg-secondary/30">
                                                    <Checkbox
                                                        id="subsidy"
                                                        checked={suggestionForm.subsidy_eligible}
                                                        onCheckedChange={(checked) => setSuggestionForm({ ...suggestionForm, subsidy_eligible: checked })}
                                                    />
                                                    <div className="grid gap-1.5 leading-none">
                                                        <Label htmlFor="subsidy" className="text-sm font-medium leading-none cursor-pointer">
                                                            PM Surya Ghar Subsidy Eligible
                                                        </Label>
                                                        <p className="text-xs text-muted-foreground">
                                                            Check if this product qualifies for government subsidy
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Description Tab */}
                                        {suggestTab === 'description' && (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Short Description</Label>
                                                    <Textarea
                                                        placeholder="Brief description for product cards (150-200 characters)"
                                                        value={suggestionForm.short_description}
                                                        onChange={(e) => setSuggestionForm({ ...suggestionForm, short_description: e.target.value })}
                                                        rows={2}
                                                        maxLength={200}
                                                    />
                                                    <p className="text-xs text-muted-foreground">{suggestionForm.short_description.length}/200 characters</p>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Full Description</Label>
                                                    <Textarea
                                                        placeholder="Detailed product description covering all features, benefits, and use cases..."
                                                        value={suggestionForm.long_description}
                                                        onChange={(e) => setSuggestionForm({ ...suggestionForm, long_description: e.target.value })}
                                                        rows={8}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Images Tab */}
                                        {suggestTab === 'images' && (
                                            <div className="space-y-6">
                                                {/* Main Image Upload */}
                                                <div className="space-y-3">
                                                    <Label>Main Product Image *</Label>
                                                    <div className="relative">
                                                        {suggestionForm.main_image_url ? (
                                                            <div className="relative w-48 h-48 rounded-lg border-2 border-dashed border-primary/50 overflow-hidden group">
                                                                <img
                                                                    src={suggestionForm.main_image_url.startsWith('/uploads')
                                                                        ? `${API}${suggestionForm.main_image_url}`
                                                                        : suggestionForm.main_image_url}
                                                                    alt="Preview"
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        className="text-white"
                                                                        onClick={() => setSuggestionForm({ ...suggestionForm, main_image_url: '' })}
                                                                    >
                                                                        <X className="w-5 h-5 mr-1" /> Remove
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <label className="flex flex-col items-center justify-center w-48 h-48 rounded-lg border-2 border-dashed border-muted-foreground/50 hover:border-primary cursor-pointer transition-colors bg-muted/20">
                                                                <input
                                                                    type="file"
                                                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                                                    className="hidden"
                                                                    onChange={async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;

                                                                        const formData = new FormData();
                                                                        formData.append('file', file);

                                                                        try {
                                                                            toast.loading('Uploading...', { id: 'upload' });
                                                                            const res = await axios.post(`${API}/api/upload/image`, formData, {
                                                                                headers: {
                                                                                    Authorization: `Bearer ${token}`,
                                                                                    'Content-Type': 'multipart/form-data'
                                                                                }
                                                                            });
                                                                            setSuggestionForm({ ...suggestionForm, main_image_url: res.data.url });
                                                                            toast.success('Image uploaded!', { id: 'upload' });
                                                                        } catch (err) {
                                                                            toast.error(err.response?.data?.detail || 'Upload failed', { id: 'upload' });
                                                                        }
                                                                    }}
                                                                />
                                                                <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                                                                <span className="text-sm text-muted-foreground text-center px-2">
                                                                    Click to upload<br />
                                                                    <span className="text-xs">JPG, PNG, WebP (max 5MB)</span>
                                                                </span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Gallery Images Upload */}
                                                <div className="space-y-3">
                                                    <Label>Gallery Images (up to 3)</Label>
                                                    <div className="grid grid-cols-3 gap-4">
                                                        {suggestionForm.gallery_images.map((url, i) => (
                                                            <div key={i} className="space-y-2">
                                                                {url ? (
                                                                    <div className="relative w-full aspect-square rounded-lg border-2 border-dashed border-primary/50 overflow-hidden group">
                                                                        <img
                                                                            src={url.startsWith('/uploads') ? `${API}${url}` : url}
                                                                            alt={`Gallery ${i + 1}`}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="text-white"
                                                                                onClick={() => updateGalleryImage(i, '')}
                                                                            >
                                                                                <X className="w-4 h-4" />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <label className="flex flex-col items-center justify-center w-full aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary cursor-pointer transition-colors bg-muted/10">
                                                                        <input
                                                                            type="file"
                                                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                                                            className="hidden"
                                                                            onChange={async (e) => {
                                                                                const file = e.target.files?.[0];
                                                                                if (!file) return;

                                                                                const formData = new FormData();
                                                                                formData.append('file', file);

                                                                                try {
                                                                                    toast.loading('Uploading...', { id: `upload-${i}` });
                                                                                    const res = await axios.post(`${API}/api/upload/image`, formData, {
                                                                                        headers: {
                                                                                            Authorization: `Bearer ${token}`,
                                                                                            'Content-Type': 'multipart/form-data'
                                                                                        }
                                                                                    });
                                                                                    updateGalleryImage(i, res.data.url);
                                                                                    toast.success('Uploaded!', { id: `upload-${i}` });
                                                                                } catch (err) {
                                                                                    toast.error(err.response?.data?.detail || 'Upload failed', { id: `upload-${i}` });
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                                                                        <span className="text-xs text-muted-foreground">Image {i + 1}</span>
                                                                    </label>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm">
                                                    <Check className="w-4 h-4 text-green-600 inline mr-2" />
                                                    <strong>Upload directly:</strong> Select images from your device. Supported formats: JPEG, PNG, WebP, GIF. Max size: 5MB per image.
                                                </div>
                                            </div>
                                        )}

                                        {/* Features Tab */}
                                        {suggestTab === 'features' && (
                                            <div className="space-y-6">
                                                <div className="space-y-2">
                                                    <Label>Key Features (Bullet Points)</Label>
                                                    <div className="space-y-2">
                                                        {suggestionForm.key_features.map((feature, i) => (
                                                            <div key={i} className="flex items-center gap-2">
                                                                <Zap className="w-4 h-4 text-primary flex-shrink-0" />
                                                                <Input
                                                                    placeholder={`Feature ${i + 1}`}
                                                                    value={feature}
                                                                    onChange={(e) => updateFeature(i, e.target.value)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setSuggestionForm({ ...suggestionForm, key_features: [...suggestionForm.key_features, ''] })}
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" /> Add Feature
                                                    </Button>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label>Technical Specifications</Label>
                                                    <div className="space-y-2">
                                                        {suggestionForm.specifications.map((spec, i) => (
                                                            <div key={i} className="grid grid-cols-2 gap-2">
                                                                <Input
                                                                    placeholder="Label (e.g., Wattage)"
                                                                    value={spec.label}
                                                                    onChange={(e) => updateSpec(i, 'label', e.target.value)}
                                                                />
                                                                <Input
                                                                    placeholder="Value (e.g., 400W)"
                                                                    value={spec.value}
                                                                    onChange={(e) => updateSpec(i, 'value', e.target.value)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setSuggestionForm({ ...suggestionForm, specifications: [...suggestionForm.specifications, { label: '', value: '' }] })}
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" /> Add Specification
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="p-6 border-t bg-muted/30 flex items-center justify-between">
                                        <div className="flex gap-2">
                                            {suggestTabs.findIndex(t => t.id === suggestTab) > 0 && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => setSuggestTab(suggestTabs[suggestTabs.findIndex(t => t.id === suggestTab) - 1].id)}
                                                >
                                                    ← Previous
                                                </Button>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button type="button" variant="outline" onClick={() => setShowSuggestModal(false)}>Cancel</Button>
                                            {suggestTabs.findIndex(t => t.id === suggestTab) < suggestTabs.length - 1 ? (
                                                <Button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setSuggestTab(suggestTabs[suggestTabs.findIndex(t => t.id === suggestTab) + 1].id);
                                                    }}
                                                >
                                                    Next →
                                                </Button>
                                            ) : (
                                                <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white px-6">
                                                    <Send className="w-4 h-4 mr-2" /> Submit for Approval
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default VendorProducts;
